import { Check } from 'lucide-react';

/**
 * The pieces every Settings screen needs.
 *
 * Settings was one 817-line page holding three unrelated jobs — brand configuration, sender
 * addresses, and a domain status nobody can edit. It is now three routes, which is what lets the
 * navigation's Administration section stop being a single row, and more importantly what gives domain
 * authentication a page of its own: it is the commonest reason a campaign will not send, and it was
 * buried two thirds of the way down a form.
 *
 * ── THE SELECTED BRAND LIVES IN THE URL ───────────────────────────────────────────
 *
 * `?brand=<id>`, not component state. Three reasons: switching between the three screens keeps the
 * brand you were configuring, the browser's back button behaves, and a link to "the domain page for
 * this brand" is something you can paste to somebody.
 */

/**
 * The brand switcher.
 *
 * A tab list rather than a dropdown: there are a handful of brands, and the point is seeing at a
 * glance which ones are not authenticated — which a collapsed dropdown hides.
 */
export function BrandSelector({
  brands,
  selectedId,
  onSelect,
}: {
  brands: { id: string; name: string }[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  if (brands.length < 2) return null;

  return (
    <div role="tablist" aria-label="Brands" className="mt-5 flex flex-wrap gap-2">
      {brands.map((brand) => (
        <button
          key={brand.id}
          type="button"
          role="tab"
          aria-selected={brand.id === selectedId}
          onClick={() => onSelect(brand.id)}
          className={
            brand.id === selectedId
              ? 'flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-1.5 text-sm font-medium text-heading'
              : 'flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-2'
          }
        >
          {brand.name}
        </button>
      ))}
    </div>
  );
}

/** Shown to anyone who can read this configuration but not change it. */
export function ReadOnlyNotice() {
  return (
    <p className="mt-3 rounded-md bg-info-bg px-3 py-2 text-sm text-info-ink">
      You can see this configuration but not change it. Ask an administrator if something here needs
      changing.
    </p>
  );
}

export function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="px-4 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-foreground">{value}</dd>
      {hint !== undefined && <dd className="mt-0.5 text-xs text-muted">{hint}</dd>}
    </div>
  );
}


/** A real checkbox with a real label, so it is operable by keyboard and announced correctly. */
export function Checkbox({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary-fill focus:ring-2 focus:ring-ring"
      />

      <div className="min-w-0">
        <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-heading">
          {checked && <Check className="h-3.5 w-3.5 text-success-ink" aria-hidden="true" />}
          {label}
        </label>

        <p className="mt-0.5 text-xs text-muted">{hint}</p>
      </div>
    </div>
  );
}

